import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAlumniDropdownOpen, setIsAlumniDropdownOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const toggleAlumniDropdown = () => {
    setIsAlumniDropdownOpen(!isAlumniDropdownOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsAlumniDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="bg-primary shadow-lg sticky top-0 z-50">
      <nav className="container mx-auto px-4 py-2">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 flex-shrink-0">
            <div className="w-14 h-14 flex items-center justify-center overflow-hidden">
              <img
                src="/images/logo.png"
                alt="Katine TTI Logo"
                className="max-w-full max-h-full w-auto h-auto object-contain"
                style={{ width: 'auto', height: 'auto', maxWidth: '100%', maxHeight: '100%' }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '<span className="text-white font-bold text-3xl">K</span>';
                }}
              />
            </div>
            <div className="hidden sm:block">
              <h1 className="text-white font-bold text-xl">Katine TTI</h1>
              <p className="text-secondary text-xs">Graduation List Management</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/" className="text-white hover:text-secondary transition-colors">Home</Link>
            <Link to="/search" className="text-white hover:text-secondary transition-colors">Search</Link>
            <Link to="/gown-request" className="text-white hover:text-secondary transition-colors">Request Gown</Link>
            <Link to="/booklets" className="text-white hover:text-secondary transition-colors">Booklet</Link>
            
            {/* Alumni Dropdown - Click-based with Right-alignment */}
            <div className="relative" ref={dropdownRef}>
              <button 
                onClick={toggleAlumniDropdown}
                className="text-white hover:text-secondary transition-colors flex items-center gap-1"
              >
                Alumni
                <svg className={`w-4 h-4 transition-transform ${isAlumniDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {isAlumniDropdownOpen && (
                <div className="absolute right-0 mt-2 bg-white rounded-lg shadow-lg z-50 flex flex-row flex-nowrap" style={{ minWidth: 'max-content' }}>
                  <Link 
                    to="/alumni" 
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors whitespace-nowrap"
                    onClick={() => setIsAlumniDropdownOpen(false)}
                  >
                    👥 Alumni Committee
                  </Link>
                  <div className="w-px bg-gray-200 self-stretch"></div>
                  <Link 
                    to="/alumni/register" 
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors whitespace-nowrap"
                    onClick={() => setIsAlumniDropdownOpen(false)}
                  >
                    📝 Register / Login
                  </Link>
                  <div className="w-px bg-gray-200 self-stretch"></div>
                  <Link 
                    to="/alumni/nomination" 
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors whitespace-nowrap"
                    onClick={() => setIsAlumniDropdownOpen(false)}
                  >
                    🗳️ Nominate
                  </Link>
                  <div className="w-px bg-gray-200 self-stretch"></div>
                  <Link 
                    to="/vote" 
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors whitespace-nowrap"
                    onClick={() => setIsAlumniDropdownOpen(false)}
                  >
                    ✅ Vote
                  </Link>
                </div>
              )}
            </div>

            {user ? (
              <>
                <Link to="/admin" className="text-white hover:text-secondary transition-colors">Dashboard</Link>
                {user.role === 'admin' && (
                  <Link to="/admin/users" className="text-white hover:text-secondary transition-colors">Users</Link>
                )}
                <button onClick={handleLogout} className="text-white hover:text-secondary transition-colors">
                  Logout
                </button>
              </>
            ) : null}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white focus:outline-none z-50 relative"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <>
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
              onClick={closeMenu}
            />

            <div className="fixed top-0 right-0 h-auto w-64 bg-primary shadow-xl z-50 md:hidden rounded-bl-lg">
              <div className="flex justify-end p-3">
                <button onClick={closeMenu} className="text-white p-1 hover:text-secondary">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="flex flex-col px-4 pb-4">
                <Link to="/" className="text-white hover:text-secondary transition-colors py-2 text-base" onClick={closeMenu}>Home</Link>
                <Link to="/search" className="text-white hover:text-secondary transition-colors py-2 text-base" onClick={closeMenu}>Search</Link>
                <Link to="/gown-request" className="text-white hover:text-secondary transition-colors py-2 text-base" onClick={closeMenu}>Request Gown</Link>
                <Link to="/booklets" className="text-white hover:text-secondary transition-colors py-2 text-base" onClick={closeMenu}>Booklet</Link>
                
                <div className="mt-2 pt-2 border-t border-primary-light">
                  <p className="text-secondary text-xs mb-2">ALUMNI</p>
                  <Link to="/alumni" className="text-white hover:text-secondary transition-colors py-2 text-base block" onClick={closeMenu}>👥 Alumni Committee</Link>
                  <Link to="/alumni/register" className="text-white hover:text-secondary transition-colors py-2 text-base block" onClick={closeMenu}>📝 Register / Login</Link>
                  <Link to="/alumni/nomination" className="text-white hover:text-secondary transition-colors py-2 text-base block" onClick={closeMenu}>🗳️ Nominate</Link>
                  <Link to="/vote" className="text-white hover:text-secondary transition-colors py-2 text-base block" onClick={closeMenu}>✅ Vote</Link>
                </div>

                {user && (
                  <div className="mt-2 pt-2 border-t border-primary-light">
                    <p className="text-secondary text-xs mb-2">ADMIN</p>
                    <Link to="/admin" className="text-white hover:text-secondary transition-colors py-2 text-base block" onClick={closeMenu}>Dashboard</Link>
                    {user.role === 'admin' && (
                      <Link to="/admin/users" className="text-white hover:text-secondary transition-colors py-2 text-base block" onClick={closeMenu}>Users</Link>
                    )}
                    <button onClick={() => { handleLogout(); closeMenu(); }} className="text-white hover:text-secondary transition-colors py-2 text-base text-left w-full">
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </nav>
    </header>
  );
};

export default Header;
